import type { ModalProps } from 'flowbite-react';
import React, { createContext, useCallback, useState } from 'react';

export type ModalComponent<CustomProps> = React.FC<ModalProps & CustomProps>;
export type ModalData<CustomProps = {}> = {
    id: string;
    component: ModalComponent<CustomProps>;
    props: CustomProps;
    onClose: () => void;
};

export type ModalContextType = {
    showModal: <CustomProps = {}>(id: string, modal: ModalComponent<CustomProps>, props: CustomProps) => void;
    hideModal: (id: string) => void;
};

export const ModalsContext = createContext<ModalContextType | undefined>(undefined);
export const ModalsStateContext = createContext<ModalData[]>([]);

export const ModalsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [modals, setModals] = useState<ModalData[]>([]);

    const hideModal = useCallback((id: string) => {
        setModals(prev => prev.filter(modal => modal.id !== id));
    }, []);

    const showModal = useCallback(<CustomProps = {}>(id: string, modal: ModalComponent<CustomProps>, props: CustomProps) => {
        if (modals.some(m => m.id === id)) {
            return;
        }

        setModals(prev => [
            ...prev,
            {
                id,
                component: modal as ModalComponent<{}>,
                props: props as {},
                onClose: () => hideModal(id)
            }
        ]);
    }, []);

    return (
        <ModalsStateContext.Provider value={modals}>
            <ModalsContext.Provider value={{ showModal, hideModal }}>
                {children}
            </ModalsContext.Provider>
        </ModalsStateContext.Provider>
    );
};
